import React, { useState, useEffect } from 'react';

interface PersonalisedLearningPreviewProps {
  variant?: 'card' | 'featured';
}

const PersonalisedLearningPreview: React.FC<PersonalisedLearningPreviewProps> = ({ variant = 'card' }) => {
  const [activeStudent, setActiveStudent] = useState(0);

  const students = [
    { name: 'Emma', avatar: 'E', style: 'Visual', pace: 'Fast', color: '#ec4899', progress: 92 },
    { name: 'Jack', avatar: 'J', style: 'Hands-on', pace: 'Steady', color: '#3b82f6', progress: 78 },
    { name: 'Mia', avatar: 'M', style: 'Reading', pace: 'Thorough', color: '#10b981', progress: 85 },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStudent(prev => (prev + 1) % students.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const current = students[activeStudent];
  const height = variant === 'featured' ? 'h-48' : 'h-32';

  return (
    <div
      className={`relative ${height} w-full rounded-xl overflow-hidden`}
      style={{
        background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
      }}
    >
      {/* Header */}
      <div
        className="px-3 py-1.5 flex items-center justify-between"
        style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}
      >
        <span className="text-[10px] font-semibold" style={{ color: '#94a3b8' }}>
          Personalised Paths
        </span>
        <div className="flex gap-1">
          {students.map((s, idx) => (
            <div
              key={s.name}
              className="w-1.5 h-1.5 rounded-full transition-all duration-300"
              style={{
                background: idx === activeStudent ? s.color : 'rgba(255, 255, 255, 0.2)',
              }}
            />
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-3">
        {/* Student profile */}
        <div className="flex items-center gap-3 mb-2">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white transition-all duration-300"
            style={{ background: current.color }}
          >
            {current.avatar}
          </div>
          <div className="flex-1">
            <div className="text-xs font-medium text-white">{current.name}'s Path</div>
            <div className="text-[9px]" style={{ color: '#64748b' }}>
              {current.style} learner • {current.pace} pace
            </div>
          </div>
          <div
            className="text-lg font-bold"
            style={{ color: current.color }}
          >
            {current.progress}%
          </div>
        </div>

        {/* Personalised content cards */}
        <div className="grid grid-cols-3 gap-1.5">
          <div
            className="p-1.5 rounded text-center transition-all duration-300"
            style={{
              background: `${current.color}15`,
              border: `1px solid ${current.color}30`,
            }}
          >
            <div className="text-sm mb-0.5">
              {current.style === 'Visual' ? '🎬' : current.style === 'Hands-on' ? '🧪' : '📖'}
            </div>
            <div className="text-[8px]" style={{ color: current.color }}>
              {current.style}
            </div>
          </div>
          <div
            className="p-1.5 rounded text-center transition-all duration-300"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <div className="text-sm mb-0.5">⏱️</div>
            <div className="text-[8px]" style={{ color: '#64748b' }}>{current.pace}</div>
          </div>
          <div
            className="p-1.5 rounded text-center transition-all duration-300"
            style={{
              background: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid rgba(16, 185, 129, 0.2)',
            }}
          >
            <div className="text-sm mb-0.5">📈</div>
            <div className="text-[8px]" style={{ color: '#10b981' }}>+12%</div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div
        className="absolute bottom-0 left-0 right-0 px-3 py-1 text-center"
        style={{ background: `${current.color}10` }}
      >
        <span className="text-[8px]" style={{ color: current.color }}>
          Content adapts to each student's needs
        </span>
      </div>
    </div>
  );
};

export default PersonalisedLearningPreview;
